import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Smartphone, Wand2, Sparkles, Plus } from 'lucide-react';

/**
 * TimeTec terminal (clock device) → local branch mapping.
 *
 * Lets the admin click "Sync from TimeTec" to pull the current list of devices
 * known to TimeTec, then assign each device serial number to a local branch.
 * Once mapped, "Resolve attendance branches" rewrites the `branch` field on
 * every TimeTec attendance record by looking up the device the user actually
 * clocked at (via TimeTec's raw audit log).
 */
export default function TimetecTerminalsCard() {
  const [terminals, setTerminals] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveDays, setResolveDays] = useState(30);
  const [resolveAll, setResolveAll] = useState(false);
  const [lastResolveResult, setLastResolveResult] = useState(null);

  // Device-based bulk mapping (suggestions from punch history)
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestDays, setSuggestDays] = useState(30);
  const [confirmedMappings, setConfirmedMappings] = useState({}); // sn → branch_id
  const [savingMappings, setSavingMappings] = useState(false);

  // Manual add device
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    terminal_sn: '',
    label: '',
    model: '',
    branch_id: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, bRes] = await Promise.all([
        axios.get(`${API}/timetec/terminals`),
        axios.get(`${API}/branches`),
      ]);
      setTerminals(tRes.data || []);
      setBranches(bRes.data || []);
    } catch (e) {
      toast.error('Failed to load terminals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const syncFromTimetec = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/timetec/terminals/sync`);
      toast.success(`Synced. ${r.data.new} new device(s), ${r.data.upserted} total.`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openSuggest = async () => {
    setSuggestOpen(true);
    setSuggestLoading(true);
    setSuggestions([]);
    setConfirmedMappings({});
    try {
      const r = await axios.post(`${API}/timetec/terminals/suggest-mappings`, null,
        { params: { days: suggestDays }, timeout: 5 * 60 * 1000 });
      const list = r.data.suggestions || [];
      setSuggestions(list);
      // Pre-fill confirmed with each suggestion (admin can override)
      const seed = {};
      list.forEach(s => {
        if (s.suggested_branch) {
          const b = branches.find(x =>
            x.name.trim().toLowerCase() === s.suggested_branch.trim().toLowerCase()
          );
          if (b) seed[s.terminal_sn] = b.id;
        }
      });
      setConfirmedMappings(seed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suggestion lookup failed');
      setSuggestOpen(false);
    } finally {
      setSuggestLoading(false);
    }
  };

  const saveBulkMappings = async () => {
    const mappings = Object.entries(confirmedMappings)
      .filter(([, bid]) => bid)
      .map(([sn, bid]) => ({ terminal_sn: sn, branch_id: bid }));
    if (mappings.length === 0) {
      toast.info('Nothing selected — pick a branch for at least one device.');
      return;
    }
    setSavingMappings(true);
    try {
      const r = await axios.post(`${API}/timetec/terminals/bulk-map`, { mappings });
      const errs = r.data.errors || [];
      if (errs.length) {
        toast.warning(`${r.data.updated} mapped · ${errs.length} error(s) — ${errs.slice(0,2).map(e => e.error).join('; ')}`);
      } else {
        toast.success(`${r.data.updated} device(s) mapped.`);
      }
      setSuggestOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk map failed');
    } finally {
      setSavingMappings(false);
    }
  };

  const openManual = () => {
    setManualForm({ terminal_sn: '', label: '', model: '', branch_id: '' });
    setManualOpen(true);
  };

  const saveManual = async () => {
    const sn = (manualForm.terminal_sn || '').trim();
    if (!sn) { toast.error('Terminal serial number is required.'); return; }
    setManualSaving(true);
    try {
      await axios.post(`${API}/timetec/terminals/manual`, {
        terminal_sn: sn,
        label: manualForm.label || null,
        model: manualForm.model || null,
        branch_id: manualForm.branch_id || null,
      });
      toast.success(`Device ${sn} added.`);
      setManualOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add device');
    } finally {
      setManualSaving(false);
    }
  };

  const updateBranch = async (sn, branchId) => {
    try {
      await axios.put(`${API}/timetec/terminals/${sn}`, {
        branch_id: branchId === '__clear__' ? '' : branchId,
      });
      toast.success('Device mapping updated');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const updateLabel = async (sn, label) => {
    try {
      await axios.put(`${API}/timetec/terminals/${sn}`, { label });
      // No toast — silent save on blur
      setTerminals(prev => prev.map(t => t.terminal_sn === sn ? { ...t, label } : t));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const runResolver = async () => {
    if (!terminals.some(t => t.branch_name)) {
      toast.error('Map at least one device to a branch first.');
      return;
    }
    const scope = resolveAll
      ? 'EVERY TimeTec attendance record on file (every distinct date in the DB)'
      : `the last ${resolveDays} day(s)`;
    if (!window.confirm(
      `Look up the device used for each clock-in over ${scope} ` +
      `and update the branch on every affected TimeTec attendance record? ` +
      `Records with admin_override=true will be skipped. ` +
      (resolveAll ? 'A full-history run can take 1-3 minutes.' : '')
    )) return;
    setResolving(true);
    try {
      const params = resolveAll
        ? { all_records: 'true' }
        : { days: resolveDays };
      const r = await axios.post(
        `${API}/timetec/resolve-branches-from-devices`,
        null,
        { params, timeout: 5 * 60 * 1000 } // 5 min for full-history runs
      );
      setLastResolveResult(r.data);
      toast.success(
        `Updated ${r.data.records_updated} record(s) across ${r.data.dates_processed} date(s) ` +
        `(seen ${r.data.records_seen}, skipped ${r.data.skipped_admin_override} override).`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Resolver failed');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4" data-testid="timetec-terminals-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-stone-600" />
          <h3 className="font-semibold text-stone-800">TimeTec Clocking Devices</h3>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={syncFromTimetec} disabled={syncing}
            data-testid="timetec-terminals-sync-button">
            <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync from TimeTec'}
          </Button>
          <Button variant="outline" size="sm" onClick={openManual}
            data-testid="add-manual-terminal-button">
            <Plus className="w-4 h-4 mr-2" />
            Add device
          </Button>
          <Button variant="outline" size="sm" onClick={openSuggest}
            data-testid="suggest-mappings-button">
            <Sparkles className="w-4 h-4 mr-2" />
            Bulk-map unmapped devices
          </Button>
        </div>
      </div>
      <p className="text-xs text-stone-500 mb-3">
        Map each physical clock-in device to a local branch. The branch on every TimeTec
        attendance record will be set from the device the user clocked at — not from the
        user&apos;s home branch — so staff who float between branches are credited correctly.
        Use <b>Add device</b> to register a serial the API can&apos;t (or won&apos;t)
        surface (retired hardware, offline devices) and pre-map it to a branch so historical
        records reference it correctly.
      </p>

      {loading && <div className="text-stone-400 text-sm">Loading…</div>}

      {!loading && terminals.length === 0 && (
        <div className="text-center py-6 text-stone-500 text-sm">
          No devices known yet. Click <b>Sync from TimeTec</b> above to pull the current list.
        </div>
      )}

      {!loading && terminals.length > 0 && (
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-700">
              <tr>
                <th className="text-left px-4 py-2">Serial</th>
                <th className="text-left px-4 py-2">ID</th>
                <th className="text-left px-4 py-2">Model</th>
                <th className="text-left px-4 py-2">Label</th>
                <th className="text-left px-4 py-2">Mapped to local branch</th>
              </tr>
            </thead>
            <tbody>
              {terminals.map(t => (
                <tr key={t.terminal_sn} className="border-t border-stone-100"
                  data-testid={`terminal-row-${t.terminal_sn}`}>
                  <td className="px-4 py-2 font-mono text-xs">{t.terminal_sn}</td>
                  <td className="px-4 py-2">{t.terminal_id}</td>
                  <td className="px-4 py-2 text-stone-500">{t.model}</td>
                  <td className="px-4 py-2">
                    <Input
                      defaultValue={t.label || ''}
                      onBlur={(e) => {
                        if (e.target.value !== (t.label || '')) {
                          updateLabel(t.terminal_sn, e.target.value);
                        }
                      }}
                      placeholder="e.g., Front counter clock"
                      className="h-8 text-xs"
                      data-testid={`terminal-label-${t.terminal_sn}`}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Select
                      value={t.branch_id || '__clear__'}
                      onValueChange={(v) => updateBranch(t.terminal_sn, v)}
                    >
                      <SelectTrigger className="h-8 w-44 text-xs"
                        data-testid={`terminal-branch-select-${t.terminal_sn}`}>
                        <SelectValue placeholder="— unmapped —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__clear__">— unmapped —</SelectItem>
                        {branches.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Resolver — rewrites branch on attendance records */}
      <div className="border-t border-stone-200 mt-4 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Wand2 className="w-4 h-4 text-emerald-700" />
          <h4 className="font-semibold text-stone-800 text-sm">Rewrite branches from device punches</h4>
        </div>
        <p className="text-xs text-stone-500 mb-3">
          For every TimeTec attendance record over the last N days, look up the device
          the user used for their first clock-in and set <b>branch</b> on the record to
          that device&apos;s mapped branch. Signed-off records <b>are</b> updated (the
          branch is metadata, not the punch times). Only records with{' '}
          <b>admin_override=true</b> are skipped.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Days back</Label>
            <Input
              type="number"
              min="1"
              max="365"
              value={resolveDays}
              onChange={(e) => setResolveDays(parseInt(e.target.value) || 30)}
              disabled={resolveAll}
              className={`w-24 h-9 ${resolveAll ? 'opacity-40' : ''}`}
              data-testid="terminals-resolve-days"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer h-9"
            data-testid="terminals-resolve-all-label">
            <input
              type="checkbox"
              checked={resolveAll}
              onChange={(e) => setResolveAll(e.target.checked)}
              data-testid="terminals-resolve-all-checkbox"
              className="w-4 h-4"
            />
            <span>All records (every date in DB)</span>
          </label>
          <Button
            onClick={runResolver}
            disabled={resolving || terminals.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="terminals-resolve-button">
            <Wand2 className={`w-4 h-4 mr-2 ${resolving ? 'animate-pulse' : ''}`} />
            {resolving
              ? (resolveAll ? 'Resolving full history…' : 'Resolving…')
              : (resolveAll ? 'Resolve all attendance branches' : 'Resolve attendance branches')}
          </Button>
        </div>
        {lastResolveResult && (
          <div className="mt-3 text-xs bg-stone-50 border border-stone-200 rounded p-3 text-stone-700"
            data-testid="terminals-resolve-result">
            <div>Range: <b>{lastResolveResult.from} → {lastResolveResult.to}</b>
              {' '}({lastResolveResult.dates_processed} date{lastResolveResult.dates_processed === 1 ? '' : 's'} processed{lastResolveResult.all_records ? ', full history' : ''})
            </div>
            <div>Records updated: <b className="text-emerald-700">{lastResolveResult.records_updated}</b> of {lastResolveResult.records_seen} seen</div>
            <div>Skipped (admin override): {lastResolveResult.skipped_admin_override}</div>
            {lastResolveResult.dates_without_punch_map?.length > 0 && (
              <div>
                Dates with no punch map: {lastResolveResult.dates_without_punch_map.length}
                <span className="text-stone-400"> (usually means no device-to-branch mapping has been set yet, or TimeTec was unreachable)</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk mapping modal */}
      <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
        <DialogContent className="max-w-3xl" data-testid="bulk-map-dialog">
          <DialogHeader>
            <DialogTitle>Bulk-map unmapped clocking devices</DialogTitle>
            <p className="text-xs text-stone-500 pt-1">
              For each device we look at the last {suggestDays} days of punches, tally which
              local branches its punchers usually belong to, and suggest the majority. Because
              staff can float between branches these are <b>suggestions only</b> — please
              confirm or override each one. Devices with 0 punches in the window won&apos;t
              have a suggestion; pick a branch manually.
            </p>
          </DialogHeader>

          <div className="flex items-end gap-2 mb-2">
            <div>
              <Label className="text-xs">Days back</Label>
              <Input type="number" min="1" max="180" value={suggestDays}
                onChange={e => setSuggestDays(Math.max(1, Math.min(180, parseInt(e.target.value) || 30)))}
                className="w-24 h-9" data-testid="bulk-map-days" />
            </div>
            <Button variant="outline" size="sm" onClick={openSuggest} disabled={suggestLoading}
              data-testid="bulk-map-refresh">
              <RefreshCw className={`w-3 h-3 mr-1 ${suggestLoading ? 'animate-spin' : ''}`} />
              {suggestLoading ? 'Analysing punches…' : 'Refresh suggestions'}
            </Button>
          </div>

          {suggestLoading && (
            <div className="text-sm text-stone-500 py-8 text-center">
              Reading TimeTec audit logs for the last {suggestDays} days… this can take a minute or two.
            </div>
          )}

          {!suggestLoading && suggestions.length > 0 && (
            <div className="overflow-y-auto max-h-[50vh] -mx-6 px-6">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-700">
                  <tr>
                    <th className="text-left px-3 py-2">Device (SN · ID · Model)</th>
                    <th className="text-left px-3 py-2">Suggestion</th>
                    <th className="text-left px-3 py-2">Assign to</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map(s => {
                    const votesText = Object.entries(s.votes || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([b, c]) => `${b}(${c})`)
                      .join(', ') || '—';
                    return (
                      <tr key={s.terminal_sn} className="border-t border-stone-100"
                        data-testid={`bulk-map-row-${s.terminal_sn}`}>
                        <td className="px-3 py-2 align-top">
                          <div className="font-mono text-xs">{s.terminal_sn}</div>
                          <div className="text-xs text-stone-500">
                            ID {s.terminal_id || '—'} · {s.model || '—'}
                            {s.current_branch && (
                              <span className="ml-1 text-emerald-700"> · already → {s.current_branch}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          {s.suggested_branch ? (
                            <>
                              <div className="text-stone-800 font-medium">
                                {s.suggested_branch}
                                <span className="ml-1 text-xs text-stone-500">
                                  ({Math.round((s.confidence || 0) * 100)}% · {s.total_punches} punches)
                                </span>
                              </div>
                              <div className="text-xs text-stone-500">Votes: {votesText}</div>
                            </>
                          ) : (
                            <div className="text-xs text-stone-500 italic">
                              No suggestion ({s.total_punches} punches, no user branches on file).
                              Pick manually →
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Select
                            value={confirmedMappings[s.terminal_sn] || '__none__'}
                            onValueChange={v => setConfirmedMappings(m => ({
                              ...m, [s.terminal_sn]: v === '__none__' ? '' : v,
                            }))}>
                            <SelectTrigger className="h-8 w-40 text-xs"
                              data-testid={`bulk-map-select-${s.terminal_sn}`}>
                              <SelectValue placeholder="— skip —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— skip —</SelectItem>
                              {branches.map(b => (
                                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!suggestLoading && suggestions.length === 0 && (
            <div className="text-sm text-stone-500 py-6 text-center">
              No devices to map. Click <b>Sync from TimeTec</b> or run the Gratuities diagnostic first.
            </div>
          )}

          <div className="flex justify-between items-center pt-2 border-t border-stone-200">
            <div className="text-xs text-stone-500">
              {Object.values(confirmedMappings).filter(v => v).length} of {suggestions.length} ready to save.
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setSuggestOpen(false)} disabled={savingMappings}
                data-testid="bulk-map-cancel">Cancel</Button>
              <Button onClick={saveBulkMappings} disabled={savingMappings}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="bulk-map-save">
                {savingMappings ? 'Saving…' : 'Save mappings'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add manual device dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md" data-testid="manual-terminal-dialog">
          <DialogHeader>
            <DialogTitle>Add clocking device manually</DialogTitle>
            <p className="text-xs text-stone-500 pt-1">
              Register a device by serial number. Useful for retired or offline devices
              that TimeTec no longer returns via its API, but which appear on historical
              attendance records that need a branch.
            </p>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Terminal Serial Number *</Label>
              <Input
                value={manualForm.terminal_sn}
                onChange={e => setManualForm({ ...manualForm, terminal_sn: e.target.value })}
                placeholder="e.g. CNYJ231260139"
                data-testid="manual-terminal-sn" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Label</Label>
                <Input value={manualForm.label}
                  onChange={e => setManualForm({ ...manualForm, label: e.target.value })}
                  placeholder="e.g. Mall front-of-house"
                  data-testid="manual-terminal-label" />
              </div>
              <div>
                <Label className="text-xs">Model</Label>
                <Input value={manualForm.model}
                  onChange={e => setManualForm({ ...manualForm, model: e.target.value })}
                  placeholder="e.g. TC10 (old)"
                  data-testid="manual-terminal-model" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Branch (optional — can be set later)</Label>
              <Select value={manualForm.branch_id || '__none__'}
                onValueChange={v => setManualForm({ ...manualForm, branch_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="manual-terminal-branch"><SelectValue placeholder="— unmapped —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unmapped —</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setManualOpen(false)} disabled={manualSaving}
                data-testid="manual-terminal-cancel">Cancel</Button>
              <Button onClick={saveManual} disabled={manualSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="manual-terminal-save">
                {manualSaving ? 'Saving…' : 'Add device'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
